import React, { useEffect, useState } from 'react'
// components
import { Box, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import Chip from '@mui/material/Chip'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'

// services
import { getShiftDetails } from 'services/api/time-attendance'
import { useQuery } from 'react-query'
import { calculateTimeDifferenceFormatted } from 'services/helper/DateTime'

// icons
import InputIcon from '@mui/icons-material/Input'
import OutputIcon from '@mui/icons-material/Output'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import CheckIcon from '@mui/icons-material/Check'
import AdxButton from 'components/custom/AdxButton'

const ShiftDetails = ({ shiftId }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [drawerState, setDrawerState] = useState({ right: false })
  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  const [workingDays, setWorkingDays] = useState([
    {
      day: 'Monday',
      label: 'Mon',
      isWorkingDay: false,
    },
    {
      day: 'Tuesday',
      label: 'Tue',
      isWorkingDay: false,
    },
    {
      day: 'Wednesday',
      label: 'Wed',
      isWorkingDay: false,
    },
    {
      day: 'Thursday',
      label: 'Thu',
      isWorkingDay: false,
    },
    {
      day: 'Friday',
      label: 'Fri',
      isWorkingDay: false,
    },
    {
      day: 'Saturday',
      label: 'Sat',
      isWorkingDay: false,
    },
    {
      day: 'Sunday',
      label: 'Sun',
      isWorkingDay: false,
    },
  ])

  const { data: shiftData } = useQuery(['getShiftDetails', shiftId], async () => {
    if (shiftId) {
      const { data } = await getShiftDetails(shiftId)
      return data
    }
  })

  const data = shiftData?.shifts
  const flexibleHours = data?.working_mode_status_id?.id === 2
  const lastClockIn = data?.late_last_clockin

  const today = data?.working_time?.find(
    (item) => item.day === new Date().toLocaleDateString('en-US', { weekday: 'long' })
  )

  // to update working days chip based on data given
  useEffect(() => {
    if (data) {
      const updatedWorkingDays = workingDays.map((workingDay) => ({
        ...workingDay,
        isWorkingDay: data.working_time.some((item) => item.day === workingDay.day),
      }))

      setWorkingDays(updatedWorkingDays)
    }
  }, [data])

  return (
    <Box>
      <AdxButton
        variant={'underlined'}
        size={'tiny'}
        onClick={() => setDrawerState({ right: true })}
      >
        <Typography sx={{ textDecoration: 'underline', color: 'alternate.main' }}>
          Shift details
        </Typography>
      </AdxButton>
      <AdxDrawerComponent
        state={drawerState}
        anchor="right"
        open={drawerState}
        onClose={handleDrawer}
        sx={{ width: isMobile ? '100vw' : '450px' }}
        headerprops
        title={'Assigned Shift'}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <TextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderWidth: 1,
                  borderColor: 'alternate.main',
                },
              },
            }}
            disabled
            defaultValue={data?.name ? `${data.name}` : ''}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography sx={{ color: 'light-gray-text.main' }}>Time period</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: 1,
                borderColor: 'gray-line.main',
                borderRadius: '4.5px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  padding: '16px',
                  width: '100%',
                  borderBottom: !flexibleHours ? 1 : null,
                  borderBottomColor: 'gray-line.main',
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'alternate.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '100%',
                    width: '45px',
                    height: '45px',
                  }}
                >
                  <TimerOutlinedIcon sx={{ color: 'alternate.main' }} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Total hours required
                  </Typography>
                  <Typography variant="h4">
                    {today?.start_time
                      ? calculateTimeDifferenceFormatted(today?.start_time, today?.end_time)
                      : today?.total_hours ?? '--:--'}
                  </Typography>
                </Box>
              </Box>
              {!flexibleHours && (
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '16px',
                      width: '100%',
                      borderRight: isMobile ? 0 : 1,
                      borderRightColor: isMobile ? null : 'gray-line.main',
                      borderBottom: isMobile ? 1 : 0,
                      borderBottomColor: isMobile ? 'gray-line.main' : null,
                      gap: '16px',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'default-background.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '100%',
                        width: '45px',
                        height: '45px',
                      }}
                    >
                      <InputIcon sx={{ color: 'alternate.main' }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Typography sx={{ color: 'light-gray-text.main' }}>Clock in time</Typography>
                      <Typography variant="h4">{`${today?.start_time ?? '--:--'}`}</Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      padding: '16px',
                      width: '100%',
                      gap: '16px',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'default-background.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '100%',
                        width: '45px',
                        height: '45px',
                      }}
                    >
                      <OutputIcon sx={{ color: 'alternate.main' }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <Typography sx={{ color: 'light-gray-text.main' }}>Clock out time</Typography>
                      <Typography variant="h4">{`${today?.end_time ?? '--:--'}`}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {flexibleHours && lastClockIn && (
                <Box
                  sx={{
                    display: 'flex',
                    padding: '16px',
                    width: '100%',
                    gap: '16px',
                    borderTop: 1,
                    borderTopColor: 'gray-line.main',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'default-background.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '100%',
                      width: '45px',
                      height: '45px',
                    }}
                  >
                    <InputIcon sx={{ color: 'alternate.main' }} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography sx={{ color: 'light-gray-text.main' }}>
                      Last clock in time
                    </Typography>
                    <Typography variant="h4">{lastClockIn ?? '--:--'}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography sx={{ color: 'light-gray-text.main' }}>Working days</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {workingDays.map((day, index) => (
                <Chip
                  disabled={!day.isWorkingDay}
                  key={index}
                  icon={<CheckIcon />}
                  label={day.label}
                  variant="outlined"
                  sx={{
                    width: '85px',
                    color: day.isWorkingDay ? 'alternate.main' : 'inherit',
                    backgroundColor: day.isWorkingDay ? 'alternate.light' : 'inherit',
                    borderColor: day.isWorkingDay ? 'alternate.main' : 'inherit',
                    '& .MuiChip-icon': {
                      color: day.isWorkingDay ? 'alternate.main' : 'inherit',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </AdxDrawerComponent>
    </Box>
  )
}

export default ShiftDetails
